import { defineComponent as _defineComponent } from 'vue'
import { normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["id"]

import { generateUID } from "@/builder/BuilderUtilities";
import { PageDesign } from "@/builder/interfaces/";
import { useRenderStore } from "@/builder/stores/renderStore";
import { Frequency } from "@/enums";
import { PayPalNamespace, loadScript } from "@paypal/paypal-js";
import { PropType, Ref, computed, onMounted, ref, watch } from "vue";
import { IPaymentComponent } from "./PaymentInterface";

// injects

export default /*@__PURE__*/_defineComponent({
  __name: 'PaypalPaymentComponent',
  props: {
    component: {
        type: Object as PropType<IPaymentComponent>,
        required: true,
    },
    readOnly: {
        type: Boolean,
        required: true,
        default: false,
    },
    payPalGatewayId: {
        type: Number as PropType<number | null>,
        required: false,
        default: null,
    },
    payPalMerchantAccount: {
        type: String as PropType<string | null>,
        required: false,
        default: null,
    },
    payPalCurrency: {
        type: String as PropType<string | null>,
        required: false,
        default: null,
    },
    pageDesign: {
        type: Object as PropType<PageDesign>,
        required: true,
    },
    formIsOnParentStep: Boolean,
},
  setup(__props) {

const renderStore = useRenderStore();

// props
const props = __props;

const payPalButtonId = ref(props.component.id + generateUID('_paypal-button'));
const payPalLoaded = ref(false);
const payPal = ref(null) as Ref<PayPalNamespace | null>;

const payPalClientId = computed(() => {
    return process.env.VUE_APP_PAYPAL_CLIENT_ID;
});

const hasPayPal = computed(() => {
    return (
        props.component.customData.enablePayPal &&
        props.payPalMerchantAccount !== null &&
        props.payPalCurrency !== null &&
        props.payPalGatewayId !== null
    );
});

const payPalDisabled = computed(() => {
    if (props.readOnly) return true;
    if (renderStore.totalAmount <= 0) return true;
    if (
        renderStore.gift.frequency !== Frequency.OneTime &&
        renderStore.gift.frequency !== null
    )
        return true;

    return false;
});

const loadPayPalElement = async () => {
    const paypalButton = document.getElementById(payPalButtonId.value);
    if (!paypalButton) return;
    if (!hasPayPal.value) return;

    if (
        !payPalClientId.value ||
        !props.payPalMerchantAccount ||
        !props.payPalGatewayId === null
    ) {
        console.error(
            "PayPal client id, merchant account, or gateway id is not set"
        );
        return;
    }

    destroyPayPalElement();

    await loadScript({
        clientId: payPalClientId.value,
        merchantId: props.payPalMerchantAccount,
        currency: renderStore.page.payPalGatewayCurrency?.toUpperCase() ?? 'USD', // paypal currency is always uppercase
        enableFunding: ["paypal", "venmo"],
        disableFunding: ['card', 'paylater'],
    })
        .then((data) => {
            payPal.value = data;
            payPalLoaded.value = true;
        })
        .catch((err) => {
            console.error("Failed to load PayPal script", err);
        });

    if (!payPalLoaded.value) return;
    if (!payPal.value) return;

    // @ts-ignore
    await payPal.value
        .Buttons({
            createOrder: function (data, actions) {
                return renderStore.createPayPalOrder();
            },
            onApprove: function (data: any, actions) {
                return renderStore.setPayPalApproval(true, data?.paymentSource);
            },
            onCancel: function (data) {
                return renderStore.setPayPalApproval(false, null);
            },
            onError: function (err) {
                return renderStore.handleFailure(err.message);
            },
            style: {
                layout: 'vertical',
                color: 'silver',
                shape: 'rect',
                label: 'paypal',
                height: 40,
                disableMaxWidth: true,
                tagline: false,
                // @ts-ignore
                borderRadius: props.pageDesign.borderRadius ? Number.parseInt(props.pageDesign.borderRadius) : 0,
            },
        })
        .render("#" + payPalButtonId.value);
};

const destroyPayPalElement = () => {
    if (payPal.value) {
        const paypalButton = document.getElementById(payPalButtonId.value);
        if (paypalButton) {
            paypalButton.innerHTML = "";
        }
    }
};

onMounted(() => {
    if ((props.formIsOnParentStep || props.readOnly) && hasPayPal.value) {
        loadPayPalElement();
    }
});

watch(
    () => props.pageDesign,
    () => {
        loadPayPalElement();
    },
    { deep: true }
);

watch(
    () => props.component.customData,
    () => {
        loadPayPalElement();
    },
    { deep: true }
);

watch(
    () => props.payPalGatewayId,
    () => {
        loadPayPalElement();
    }
);

watch(
    () => props.formIsOnParentStep,
    async (newVal) => {
        if (newVal && hasPayPal.value) {
            loadPayPalElement();
        }
    }
);

watch(hasPayPal, async (newVal) => {
    if (newVal) {
        loadPayPalElement();
    } else {
        destroyPayPalElement();
    }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    id: payPalButtonId.value,
    class: _normalizeClass(["paypal-button-container", { 'paypal-disabled': payPalDisabled.value }])
  }, null, 10, _hoisted_1))
}
}

})